"use client";

import { useEffect, useMemo, useRef } from "react";
import { usePathname } from "next/navigation";

import { TouchpointGuidesDialog } from "@/components/guides/GuidesDialog";
import { RenewalSonner } from "@/components/payment/RenewalSonner";
import { VipRenewalIncentiveDialog } from "@/components/payment/VipRenewalIncentiveDialog";
import { useGuides, usePayment, useUser } from "@/stores/appStore";
import {
  getTouchpointGuides,
  TouchpointItem,
} from "@/stores/slices/guideSlice";

function getTouchpointsPage(pathname: string) {
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";
  if (normalizedPathname === "/papers") {
    return "papers";
  }
  if (normalizedPathname === "/summarizes") {
    return "summarizes";
  }
  const [firstSegment] = normalizedPathname.split("/").filter(Boolean);
  return firstSegment || "*";
}

function getPaymentTypeFromTouchpoint(item: TouchpointItem) {
  if (item.payload?.cta?.tab === "upgradePlus3x") {
    return "upgradePlus3x";
  }
  if (item.payload?.cta?.tab === "recharge") {
    return "recharge";
  }
  return "upgrade";
}

function isBuyingTouchpoint(item?: TouchpointItem) {
  return item?.type === "buying" || getTouchpointGuides(item)?.type === "buying";
}

function TouchpointPaymentRunner({
  item,
  onDismiss,
}: {
  item: TouchpointItem;
  onDismiss: (item: TouchpointItem) => Promise<void>;
}) {
  const { openPayment } = usePayment();
  const executedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (executedIdsRef.current.has(item.id)) {
      return;
    }
    executedIdsRef.current.add(item.id);
    openPayment({
      open: true,
      type: getPaymentTypeFromTouchpoint(item),
    }, () => {
      void onDismiss(item);
    });
  }, [item, onDismiss, openPayment]);

  return null;
}

function TouchpointModalHost({
  item,
  onDismiss,
}: {
  item?: TouchpointItem;
  onDismiss: (item: TouchpointItem) => Promise<void>;
}) {
  const guides = getTouchpointGuides(item);
  if (!item) {
    return null;
  }
  if (item.type === "channel_survey") {
    return null;
  }
  if (isBuyingTouchpoint(item)) {
    return (
      <TouchpointPaymentRunner
        item={item}
        onDismiss={onDismiss}
      />
    );
  }
  if (item.type === "first_offer" || item.type === "recovery_offer") {
    return (
      <VipRenewalIncentiveDialog
        item={item}
        onDismiss={onDismiss}
      />
    );
  }
  if (guides) {
    return (
      <TouchpointGuidesDialog
        item={item}
        onDismiss={onDismiss}
      />
    );
  }
  console.warn("Unknown modal touchpoint:", item);
  return null;
}

export function TouchpointsController() {
  const pathname = usePathname();
  const page = useMemo(() => getTouchpointsPage(pathname), [pathname]);
  const {
    user,
  } = useUser();
  const {
    touchpointSlots,
    fetchTouchpoints,
    fetchGlobalTouchpoints,
    dismissTouchpoint,
    clearTouchpoints,
  } = useGuides();

  useEffect(() => {
    if (!user?.userId) {
      clearTouchpoints();
      return;
    }
    void fetchTouchpoints(page);
    void fetchGlobalTouchpoints();
  }, [clearTouchpoints, fetchGlobalTouchpoints, fetchTouchpoints, page, user?.userId]);

  return (
    <>
      <TouchpointModalHost
        item={touchpointSlots.modal}
        onDismiss={async (item) => {
          await dismissTouchpoint(item, { refresh: true });
        }}
      />
      {touchpointSlots.footerAd?.type === "vip_expire_soon" ? (
        <RenewalSonner
          item={touchpointSlots.footerAd}
          onDismiss={async (item) => {
            await dismissTouchpoint(item);
          }}
        />
      ) : null}
    </>
  );
}
